import { useState } from 'react';
import { useApiProblem } from '@/shared/api';
import { Button, EmptyState, ImagePickerButton } from '@/shared/components';
import { useUploadQueue } from '@/shared/hooks/useUploadQueue';
import { skippedText } from '@/shared/lib/uploadSummary';
import { resourceImageLoadFailureText } from './resourceImageText';
import { ResourceImageTile } from './ResourceImageTile';
import { useResourceImages } from './useResourceImages';

/**
 * Fotos einer Ressource für Extras & Erlebnisse: hinzufügen, sortieren, entfernen. Das erste ist
 * das Titelbild im Gastportal. Fehler stehen als Satz im Panel, nicht nur als flüchtiger Toast; ein
 * gescheitertes Neuladen lässt die zuletzt geladene Liste stehen und sagt es.
 */
export function ResourceImagePanel({
  groupId,
  resourceId,
}: {
  groupId: string;
  resourceId: string;
}) {
  const { query, upload, remove, move, failure } = useResourceImages(groupId, resourceId);
  const problem = useApiProblem(query.error);
  const queue = useUploadQueue();
  const [skipped, setSkipped] = useState<string | null>(null);
  const reload = () => void query.refetch();

  if (query.isLoading) return <p className="text-ink-soft text-sm">Lade Fotos…</p>;
  if (!query.data) {
    return (
      <EmptyState
        icon="Error"
        title="Fotos konnten nicht geladen werden"
        description={problem ? resourceImageLoadFailureText(problem) : undefined}
        action={{ label: 'Erneut laden', onClick: reload }}
      />
    );
  }
  const { images, maxImages } = query.data;
  const full = images.length >= maxImages;
  const busy = move.isPending || remove.isPending || queue.busy;
  const addImages = async (files: File[]) => {
    setSkipped(null);
    const outcome = await queue.run(files, maxImages - images.length, (file) =>
      upload.mutateAsync(file),
    );
    if (outcome.skipped > 0) {
      setSkipped(skippedText(outcome.skipped, `Höchstens ${maxImages} Fotos je Ressource`));
    }
  };

  return (
    <div className="@container space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-ink-soft text-sm">
          {images.length === 0
            ? 'Noch keine Fotos. Gäste sehen dann einen ruhigen Ersatz.'
            : `${images.length} von ${maxImages} Fotos — das erste ist das Titelbild.`}
        </p>
        <ImagePickerButton
          label="Fotos hinzufügen"
          className="min-h-11"
          progress={queue.progress}
          disabled={full}
          onFiles={(files) => void addImages(files)}
        />
      </div>
      {full && (
        <p className="text-ink-soft text-[13px]">Höchstens {maxImages} Fotos je Ressource.</p>
      )}
      {skipped && (
        <p role="status" className="text-ink-soft text-[13px]">
          {skipped}
        </p>
      )}
      {query.isError && (
        <p role="status" className="text-ink-soft flex flex-wrap items-center gap-2 text-[13px]">
          Zeigt den zuletzt geladenen Stand – das Neuladen ist gescheitert.
          <Button type="button" size="sm" variant="ghost" className="min-h-11" onClick={reload}>
            Erneut laden
          </Button>
        </p>
      )}
      {failure && (
        <p role="alert" className="text-sealwax text-sm">
          {failure}
        </p>
      )}
      {images.length > 0 && (
        <ul className="@xs:grid-cols-2 @md:grid-cols-3 grid grid-cols-1 gap-3">
          {images.map((image, index) => (
            <ResourceImageTile
              key={image.id}
              groupId={groupId}
              resourceId={resourceId}
              imageId={image.id}
              number={index + 1}
              first={index === 0}
              last={index === images.length - 1}
              busy={busy}
              onMove={(direction) => move.mutate({ imageId: image.id, direction })}
              onRemove={() => remove.mutate(image.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
